import { CompanyDto, EmployeeDto } from './web-api-client';
import { authorizedFetch } from './auth/keycloakProvider';

/**
 * Dashboard statistics DTO
 */
export interface DashboardStatsDto {
  totalDocuments: number;
  inTransitDocuments: number;
  signedDocuments: number;
  deliveredDocuments: number;
  pendingDocuments: number;
  totalCompanies: number;
  totalEmployees: number;
  previousTotalDocuments?: number;
  previousInTransitDocuments?: number;
  previousSignedDocuments?: number;
  previousDeliveredDocuments?: number;
  previousPendingDocuments?: number;
  previousTotalCompanies?: number;
  previousTotalEmployees?: number;
  ecmrsByMonth: EcmrByMonthDto[];
  ecmrsByStatus: EcmrByStatusDto[];
  recentDocuments: RecentDocumentDto[];
}

export interface EcmrByMonthDto {
  month: string;
  count: number;
}

export interface EcmrByStatusDto {
  status: string;
  count: number;
}

export interface RecentDocumentDto {
  id: string;
  documentUUID: string;
  documentNumber: string;
  companyName: string;
  originAddress: string;
  destinationAddress: string;
  status: number; // DocumentStatus enum value
  created: string; // ISO date string
}

/**
 * Get dashboard statistics
 */
export const getDashboardStats = async (): Promise<DashboardStatsDto> => {
  const response = await authorizedFetch('/api/Dashboard');
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to load dashboard statistics' }));
    throw new Error(error.description || error.message || 'Failed to load dashboard statistics');
  }
  
  return await response.json();
};

// Custom document upload function that properly handles multipart/form-data
// The NSwag generated client doesn't handle multipart with mixed fields well
export interface UploadDocumentParams {
  xmlFile: File;
  xmlContent: string;
}

export interface UploadDocumentResult {
  documentId: number;
  documentUUID?: string;
  requestUUID?: string;
}

export const uploadDocument = async (params: UploadDocumentParams): Promise<UploadDocumentResult> => {
  const formData = new FormData();
  formData.append('xmlFile', params.xmlFile);
  formData.append('xmlContent', params.xmlContent);

  const response = await authorizedFetch('/api/documents/upload', {
    method: 'POST',
    body: formData,
    // Don't set Content-Type header - browser will set it with boundary for multipart
  });

  if (!response.ok) {
    const errorData = await response.json().catch((ex) => ({ message: ex }));
    var error = Error(errorData.message || `Upload failed with status ${response.status}`);
    (error as any).data = errorData; //SMALL FIX TO HAVE CURRENT XML ERROR. TODO: NEED TO MAKE THE ERRORS TYPED
    throw error;
  }

  return await response.json();
};

// Custom document update function that properly handles multipart/form-data
export interface UpdateDocumentParams {
  documentUUID: string;
  xmlFile: File;
  xmlContent: string;
}

export interface UpdateDocumentResult {
  documentId: number;
  documentUUID: string;
  requestUUID?: string;
  version: number;
}

export const updateDocument = async (params: UpdateDocumentParams): Promise<UpdateDocumentResult> => {
  const formData = new FormData();
  formData.append('xmlFile', params.xmlFile);
  formData.append('xmlContent', params.xmlContent);

  const response = await authorizedFetch(`/api/documents/${params.documentUUID}/update`, {
    method: 'PUT',
    body: formData,
    // Don't set Content-Type header - browser will set it with boundary for multipart
  });

  if (!response.ok) {
    const errorData = await response.json().catch((ex) => ({ message: ex }));
    var error = Error(errorData.message || `Update failed with status ${response.status}`);
    (error as any).data = errorData; //SMALL FIX TO HAVE CURRENT XML ERROR. TODO: NEED TO MAKE THE ERRORS TYPED
    throw error;
  }

  return await response.json();
};

// Get documents with pagination support
export interface GetDocumentsParams {
  page?: number;
  pageSize?: number;
  searchKeyword?: string;
}

export interface PagedDocumentResult {
  items: DocumentDto[];
  metadata?: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    pageSize: number;
  };
}

export interface DocumentDto {
  id: string;
  documentUUID: string;
  documentNumber: string;
  versions: number;
  status: string;
  originAddress: string;
  destinationAddress: string;
  expectedDeliveryDate?: string;
  actualDeliveryDate?: string;
  cargoDescription: string;
  cargoWeight?: number;
  cargoPackaging?: string;
  senderName?: string;
  receiverName?: string;
  notes?: string;
  companyId: number;
  clientId: number;
  created: string;
}

export const getDocuments = async (params: GetDocumentsParams = {}): Promise<PagedDocumentResult> => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
  if (params.searchKeyword) {
    queryParams.append('searchKeyword', params.searchKeyword);
  }

  const url = `/api/documents${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  const response = await authorizedFetch(url, {
    method: 'GET',
    headers: { 'Accept': 'application/json' }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to get documents' }));
    throw new Error(errorData.message || `Failed to get documents with status ${response.status}`);
  }

  return await response.json();
};

// Get companies with pagination support
export interface GetCompaniesParams {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
}

export interface PagedCompanyResult {
  items: CompanyDto[];
  metadata?: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    pageSize: number;
  };
}

export const getCompaniesPaginated = async (params: GetCompaniesParams = {}): Promise<PagedCompanyResult> => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
  if (params.searchTerm) {
    queryParams.append('filter.companyName', params.searchTerm);
  }

  const url = `/api/Companies${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  const response = await authorizedFetch(url, {
    method: 'GET',
    headers: { 'Accept': 'application/json' }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to get companies' }));
    throw new Error(errorData.message || `Failed to get companies with status ${response.status}`);
  }

  return await response.json();
};

// Get employees with pagination support
export interface GetEmployeesParams {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  companyId?: number;
  role?: string;
}

export interface PagedEmployeeResult {
  items: EmployeeDto[];
  metadata?: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    pageSize: number;
  };
}

export const getEmployeesPaginated = async (params: GetEmployeesParams = {}): Promise<PagedEmployeeResult> => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
  if (params.searchTerm) {
    // Search by name or email
    queryParams.append('filter.fullName', params.searchTerm);
  }
  if (params.companyId) {
    queryParams.append('filter.companyId', params.companyId.toString());
  }
  if (params.role) {
    queryParams.append('filter.role', params.role);
  }

  const url = `/api/Companies/employees${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  const response = await authorizedFetch(url, {
    method: 'GET',
    headers: { 'Accept': 'application/json' }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to get employees' }));
    throw new Error(errorData.message || `Failed to get employees with status ${response.status}`);
  }

  return await response.json();
};

// Get a single employee by ID
export const getEmployee = async (id: number): Promise<EmployeeDto> => {
  const response = await authorizedFetch(`/api/Companies/employees/${id}`, {
    method: 'GET',
    headers: { 'Accept': 'application/json' }
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Employee not found');
    }
    const errorData = await response.json().catch(() => ({ message: 'Failed to get employee' }));
    throw new Error(errorData.message || `Failed to get employee with status ${response.status}`);
  }

  return await response.json();
};

// Edit an employee
export interface EditEmployeeParams {
  employeeId: number;
  fullName: string;
  phoneNumber?: string;
  restrictionLevel: number;
}

export interface EditEmployeeResult {
  requestUUID?: string;
  employeeId?: string;
}

export const editEmployee = async (params: EditEmployeeParams): Promise<EditEmployeeResult> => {
  const response = await authorizedFetch(`/api/Companies/employees/${params.employeeId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      fullName: params.fullName,
      phoneNumber: params.phoneNumber,
      restrictionLevel: params.restrictionLevel,
    }),
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Employee not found');
    }
    const errorData = await response.json().catch(() => ({ message: 'Failed to update employee' }));
    throw new Error(errorData.message || `Failed to update employee with status ${response.status}`);
  }

  return await response.json();
};

// Get a single company by ID
export const getCompany = async (id: number): Promise<CompanyDto> => {
  const response = await authorizedFetch(`/api/Companies/${id}`, {
    method: 'GET',
    headers: { 'Accept': 'application/json' }
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Company not found');
    }
    const errorData = await response.json().catch(() => ({ message: 'Failed to get company' }));
    throw new Error(errorData.message || `Failed to get company with status ${response.status}`);
  }

  return await response.json();
};
